export const ENTITY_TYPES = [
  'account',
  'incomeSource',
  'category',
  'income',
  'expense',
  'payment',
  'transfer',
  'adjustment',
  'recurrenceRule',
  'recurrenceException',
  'appSettings'
] as const;

export type EntityType = (typeof ENTITY_TYPES)[number];
export type ISODateTime = string;
export type LocalDay = string;
export type Money = bigint;

export interface BaseRecord {
  id: string;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  deletedAt?: ISODateTime;
  version: number;
}

export interface Account extends BaseRecord {
  name: string;
  kind: 'bank' | 'cash';
  iconName: string;
  colorHex: string;
  isArchived: boolean;
}

export interface IncomeSource extends BaseRecord {
  name: string;
  colorHex: string;
  isArchived: boolean;
  mergedIntoID?: string;
}

export interface Category extends BaseRecord {
  name: string;
  purpose: 'personal' | 'company';
  parentID?: string;
  iconName: string;
  isArchived: boolean;
  mergedIntoID?: string;
}

export interface Income extends BaseRecord {
  amount: Money;
  accountID: string;
  sourceID: string;
  receivedAt: ISODateTime;
  reference: string;
  note: string;
}

export interface Expense extends BaseRecord {
  amount: Money;
  purpose: 'personal' | 'company';
  categoryID: string;
  merchant: string;
  purchasedOn: LocalDay;
  dueOn?: LocalDay;
  recurrenceRuleID?: string;
  logicalOccurrenceKey?: string;
  note: string;
  canceledAt?: ISODateTime;
  cancellationReason?: string;
}

export interface Payment extends BaseRecord {
  expenseID: string;
  accountID: string;
  amount: Money;
  paidAt: ISODateTime;
}

export interface Transfer extends BaseRecord {
  sourceAccountID: string;
  destinationAccountID: string;
  amount: Money;
  occurredAt: ISODateTime;
  note: string;
}

export interface Adjustment extends BaseRecord {
  accountID: string;
  amount: Money;
  kind: 'opening' | 'correction';
  occurredAt: ISODateTime;
  note: string;
}

export type RecurrenceCadence = 'weekly' | 'monthly' | 'yearly';
export type RecurrenceKind = 'subscription' | 'installment';
export type RecurrenceState = 'active' | 'paused' | 'ended' | 'completed';
export type RecurrenceExceptionReason = 'deletedOccurrence' | 'paused' | 'ended' | 'canceledInstallments';

export interface RecurrenceRule extends BaseRecord {
  anchor: LocalDay;
  cadence: RecurrenceCadence;
  interval: number;
  endOn?: LocalDay;
  occurrenceCount?: number;
  kind: RecurrenceKind;
  state: RecurrenceState;
  amount: Money;
  purpose: 'personal' | 'company';
  categoryID: string;
  merchant: string;
  note: string;
  pausedOn?: LocalDay;
  endedOn?: LocalDay;
}

export interface RecurrenceException extends BaseRecord {
  recurrenceRuleID: string;
  startsOn: LocalDay;
  endsOn: LocalDay;
  reason: RecurrenceExceptionReason;
}

export interface AppSettings extends BaseRecord {
  dueDayTimes: [string, string, string];
  schemaVersion: number;
}

export interface EntityMap {
  account: Account;
  incomeSource: IncomeSource;
  category: Category;
  income: Income;
  expense: Expense;
  payment: Payment;
  transfer: Transfer;
  adjustment: Adjustment;
  recurrenceRule: RecurrenceRule;
  recurrenceException: RecurrenceException;
  appSettings: AppSettings;
}

export type AnyEntity = EntityMap[EntityType];

export interface LedgerSnapshot {
  accounts: Account[];
  incomeSources: IncomeSource[];
  categories: Category[];
  incomes: Income[];
  expenses: Expense[];
  payments: Payment[];
  transfers: Transfer[];
  adjustments: Adjustment[];
  recurrenceRules: RecurrenceRule[];
  recurrenceExceptions: RecurrenceException[];
  settings: AppSettings;
}

export const entityArrayKey: Record<EntityType, keyof LedgerSnapshot> = {
  account: 'accounts',
  incomeSource: 'incomeSources',
  category: 'categories',
  income: 'incomes',
  expense: 'expenses',
  payment: 'payments',
  transfer: 'transfers',
  adjustment: 'adjustments',
  recurrenceRule: 'recurrenceRules',
  recurrenceException: 'recurrenceExceptions',
  appSettings: 'settings'
};

export function createBaseRecord(now = new Date().toISOString()): BaseRecord {
  return { id: crypto.randomUUID(), createdAt: now, updatedAt: now, version: 0 };
}

export function defaultSettings(now = new Date().toISOString()): AppSettings {
  return {
    ...createBaseRecord(now),
    id: '00000000-0000-4000-8000-000000000001',
    dueDayTimes: ['09:00', '13:00', '18:00'],
    schemaVersion: 1
  };
}

export function emptySnapshot(): LedgerSnapshot {
  return {
    accounts: [],
    incomeSources: [],
    categories: [],
    incomes: [],
    expenses: [],
    payments: [],
    transfers: [],
    adjustments: [],
    recurrenceRules: [],
    recurrenceExceptions: [],
    settings: defaultSettings()
  };
}

export function isDeleted(record: BaseRecord): boolean {
  return record.deletedAt !== undefined;
}

